<template>
          <v-dialog :model-value="showSummaryReportDialog" @update:model-value="updateDialog" max-width="600">
            <v-card>
                <v-card-title class="text-h6">
                    {{ t('downloadReport') }}
                </v-card-title>
                <v-card-text>
                    <!-- Report Mode Selection -->
                    <v-btn-toggle
                        v-model="selectedMode"
                        mandatory
                        class="mb-4"
                        :disabled="isGeneratingReport"
                    >
                        <v-btn value="grid">
                            <v-icon start>mdi-grid</v-icon>
                            {{ t('grid') }}
                        </v-btn>
                        <v-btn value="matrix">
                            <v-icon start>mdi-numeric</v-icon>
                            {{ t('numeric') }}
                        </v-btn>
                    </v-btn-toggle>

                    <!-- Report Type Selection - Only shown for Matrix mode -->
                    <v-select
                        v-if="selectedMode === 'matrix'"
                        v-model="selectedReportType"
                        :items="reportTypes"
                        :label="t('selectReportType')"
                        class="mb-4"
                        :disabled="isGeneratingReport"
                        item-title="title"
                        item-value="value"
                    >
                        <template v-slot:selection="{ item }">
                            <v-icon :icon="item.raw.icon" class="me-2" />
                            {{ item.raw.title }}
                        </template>
                        <template v-slot:item="{ props, item }">
                            <v-list-item v-bind="props">
                                <template v-slot:prepend>
                                    <v-icon :icon="item.raw.icon" class="me-2" />
                                </template>
                            </v-list-item>
                        </template>
                    </v-select>

                    <!-- File Format Selection -->
                    <v-select
                        v-model="selectedFileFormat"
                        :items="fileFormats"
                        :label="t('selectFileFormat')"
                        class="mb-4"
                        :disabled="isGeneratingReport || (selectedMode === 'matrix' && !selectedReportType)"
                        item-title="title"
                        item-value="value"
                    >
                        <template v-slot:selection="{ item }">
                            <v-icon :icon="item.raw.icon" class="me-2" />
                            {{ item.raw.title }}
                        </template>
                        <template v-slot:item="{ props, item }">
                            <v-list-item v-bind="props">
                                <template v-slot:prepend>
                                    <v-icon :icon="item.raw.icon" class="me-2" />
                                </template>
                            </v-list-item>
                        </template>
                    </v-select>

                    <!-- Page Size Selection - Only shown for applicable formats -->
                    <v-select
                        v-if="showPageSizeOption"
                        v-model="selectedPageSize"
                        :items="pageSizes"
                        :label="t('selectPageSize')"
                        class="mb-4"
                        :disabled="isGeneratingReport"
                        item-title="title"
                        item-value="value"
                    >
                        <template v-slot:selection="{ item }">
                            <v-icon icon="mdi-file-document-outline" class="me-2" />
                            {{ item.raw.title }}
                        </template>
                        <template v-slot:item="{ props }">
                            <v-list-item v-bind="props">
                                <template v-slot:prepend>
                                    <v-icon icon="mdi-file-document-outline" class="me-2" />
                                </template>
                            </v-list-item>
                        </template>
                    </v-select>

                    <!-- Generate Report Button -->
                    <v-btn
                        block
                        color="primary"
                        :loading="isGeneratingReport"
                        :disabled="!isFormValid"
                        @click="generateReport"
                        class="mt-4"
                    >
                        <v-icon start icon="mdi-file-download"></v-icon>
                        {{ t('generateReport') }}
                    </v-btn>

                    <v-progress-linear
                        v-if="isGeneratingReport"
                        indeterminate
                        color="primary"
                        class="mt-4"
                    ></v-progress-linear>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="grey"
                        variant="text"
                        @click="closeDialog"
                        :disabled="isGeneratingReport"
                    >
                        {{ t('cancel') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
</template>

<script setup>
    import { ref, computed, defineProps, defineEmits } from 'vue';
    import { useStore } from 'vuex';
    import { useAnalytics } from '@/utils/useAnalytics'
    import { useI18n } from 'vue-i18n';
    
    import PaginatedReportGenerator from '@/utils/PaginatedReportGenerator';
    import ApiClient from '@/ApiClient';

    const props = defineProps({
        showSummaryReportDialog: {
            type: Boolean,
            required: true
        }
    });

    const store = useStore();
    const { t } = useI18n();
    const { trackEvent } = useAnalytics()

    const emit = defineEmits(['update:showSummaryReportDialog']);

    const updateDialog = (value) => {
        emit('update:showSummaryReportDialog', value);
    };

    const closeDialog = () => {
        emit('update:showSummaryReportDialog', false);
    };

    const settings = computed(() => store.state.settings);
    const conn = store.getters.getDBConnection;
    const logoUrl = computed(() => store.getters.getLogoUrl);

    const selectedMode = ref('grid')
    const selectedReportType = ref('By Category Subcategory')
    const selectedFileFormat = ref('PDF')
    const selectedPageSize = ref('A4')
    const isGeneratingReport = ref(false)
    

    const reportTypes = [
        { title: t('byCategory'), value: 'By Category Subcategory', icon: 'mdi-shape-outline' },
        { title: t('byDrawingLocation'), value: 'By Drawing Location', icon: 'mdi-map-marker-outline' },
        { title: t('byDrawingAssignee'), value: 'By Drawing Assignee', icon: 'mdi-account-outline' },
        { title: t('byController'), value: 'By Controller', icon: 'mdi-gauge' }
    ]

    const fileFormats = [
        { title: 'PDF', value: 'PDF', icon: 'mdi-file-pdf-box' },
        { title: 'Word', value: 'WORDOPENXML', icon: 'mdi-microsoft-word' },
        { title: 'Excel', value: 'EXCELOPENXML', icon: 'mdi-microsoft-excel' },    
        { title: 'CSV', value: 'CSV', icon: 'mdi-file-delimited' },
        { title: 'HTML5', value: 'HTML5', icon: 'mdi-language-html5' },
        { title: 'IMAGE (tiff)', value: 'IMAGE', icon: 'mdi-file-image' },    
        { title: 'XML', value: 'XML', icon: 'mdi-file-code' }
    ]

    const pageSizes = [
        { title: 'A4', value: 'A4' },
        { title: 'A3', value: 'A3' }
    ]

    const showPageSizeOption = computed(() => {
        const spreadsheetFormats = ['EXCEL', 'EXCELOPENXML', 'CSV', 'XML']
        return !spreadsheetFormats.includes(selectedFileFormat.value)
    })

    const isFormValid = computed(() => {
        if (!selectedFileFormat.value) return false
        if (selectedMode.value === 'matrix' && !selectedReportType.value) return false
        if (showPageSizeOption.value && !selectedPageSize.value) return false
        return true
    })

    const getCompanyURL = (clientId) => {
        const apiUrl = store.getters.getApiUrl
        return `${apiUrl}/${clientId.toLowerCase()}`
    }

    const generateItemURL = (itemId, subItemId) => {
        const apiUrl = store.getters.getApiUrl
        const projectCode = store.getters.getProjectCode
        return `${apiUrl}/projects/${projectCode}/items/${itemId}/sub-items/${subItemId}`
    }

    const generateReport = async () => {
        isGeneratingReport.value = true
        let reportName
        
        const itemsQuery = `SELECT 
                        i.id as id,
                        i.clientId as clientId,
                        ft.title as subItem,
                        i.SubItemId as subItemId,
                        i.closedDate as closedDate,
                        i.createdDate as createdDate,
                        l.drawingId as drawingId,
                        l.drawingTitle as drawing,
                        l.title as location,
                        COALESCE(l.id, '#') as locationId,
                        i.StatusCreatedDate as statusCreatedDate,
                        st.title as status,
                        it.title as template,
                        t1.name as controlledByTeam,
                        i.AssigneeCreatedDate as assigneeCreatedDate,
                        '' as profile,
                        t2.name as assigneeTeam,
                        it.categoryTitle as category,
                        it.subcategoryTitle as subCategory,
                        it.subCategoryId as subCategoryId,
                        COALESCE(st.color, '#FFE0E0') as color,
                        0 as sort,
                        i.type                    
                    FROM filteredItems i
                    LEFT JOIN item_templates it ON i.profileId = it.id
                    LEFT JOIN form_templates ft ON i.SubItemTemplateId = ft.id
                    LEFT JOIN status_templates st ON i.StatusId = st.id
                    LEFT JOIN locations l ON i.locationId = l.id
                    LEFT JOIN teams t1 ON i.controlledByTeamId = t1.id
                    LEFT JOIN teams t2 ON i.AssigneeTeamId = t2.id
                `;
        
            const result = await conn.query(itemsQuery);
            const rows = result.toArray().map(row => {
                const jsonRow = row.toJSON();
                return {
                    ...jsonRow,
                    companyURL: getCompanyURL(jsonRow.clientId),
                    project: store.getters.getProjectName,
                    exchangeToken:'',
                    url: generateItemURL(jsonRow.id, jsonRow.id),
                    CONCAT: `${jsonRow.clientId}${jsonRow.id}`,
                    subItem: jsonRow.subItem || ''
                };
            });

        if (selectedMode.value === 'grid') {
            reportName = 'RDrive - Item Report'
            const pageSize = showPageSizeOption.value ? ` - ${selectedPageSize.value}` : ' - Excel'
            reportName += pageSize
        } else {
            reportName = `RDrive - Summary Report (Count by Form) - ${selectedReportType.value}`
            const pageSize = showPageSizeOption.value ? ` - ${selectedPageSize.value}` : ' - Excel'
            reportName += pageSize
        }
        
        downloadSummaryReport(reportName, selectedFileFormat.value, selectedMode.value === 'matrix' ? selectedReportType.value : 'Grid', rows)
    }

    const downloadSummaryReport = async (reportName, fileFormat, reportType, rows) => {
        isGeneratingReport.value = true
        try {

            trackEvent('downloadReport', {
                action_type: 'button_click',
                apiUrl: store.state.apiClient.apiUrl,
                project_code: store.getters.getProjectCode,
                project_name: store.getters.getProjectName,
                report_name: reportName,
                file_format: fileFormat,
                report_length: rows.length
            })

            const apiClient = new ApiClient('paginated-report-service.rdrive.io');
            apiClient.setApiUrl(store.state.apiClient.apiUrl);
            apiClient.setProjectCode(store.state.apiClient.projectCode);
            apiClient.setScopedToken(store.state.apiClient.scopedToken);

            const reportGenerator = new PaginatedReportGenerator(apiClient);

            const myLogoUrl = logoUrl.value ? logoUrl.value : 'https://paginated-report-service.rdrive.io/assets/logo.png';

            const reportData = {
                ItemsLatestData:rows,
                ProjectInfo:[
                    {
                        displayName:settings.value.projectName,
                        logoURL:myLogoUrl,
                        timeZone:'UTC'
                    }
                ],
                Translation:[
                    {
                        locale:"",
                        subheader:t('summaryReport') + ' ' + reportType,
                        category:t('category'),
                        subcategory:t('subcategory'),
                        controller:t('controllers'),
                        drawing:t('drawings'),
                        assignee:t('assignedTo'),
                        location:t('location'),
                        item:t('item'),
                        subitem:t('subItem'),
                        total:t('total'),
                        updatedDate:t('updatedDate'),
                        status:t('status'),
                        footerReportGen:t('reportGenerated') + ': '
                    }
                ]
            };

            await reportGenerator.generateReport({
                reportName: reportName,
                fileFormat: fileFormat,
                parameters: [
                    {
                    key: "CompanyURLParam",
                    value: 'https://' + store.state.apiClient.apiUrl
                    },
                    {
                    key: "ProjectParam",
                    value: store.state.apiClient.projectCode
                    }
                ],
                data: reportData
            });

        } catch (error) {
            // Handle error
            console.error('Error generating report:', error)
        } finally {
            isGeneratingReport.value = false;
            emit('update:showSummaryReportDialog', false);
        }
    }

   

</script>